import React, { useState, useEffect, useMemo } from "react";
import { Form, Select } from "antd";
import styled from "styled-components";
import { useUploadedFiles } from "../../store/UploadedFileProvider";
import { useToast } from "../../store/ToastProvider";
import { usePost } from "../../hooks/usePost";
import { useValidation } from "../../hooks/useValidation";
import { greyText, darkGreen } from "../../styles";
import { CompassParagraph } from "../../components/common/styledComponents";
import { MESSAGES } from "../../messages";
import calculatingAnimation from "../../assets/lottie/calculating_animation_2.json";
import RunJobModalImage from "../../assets/images/calculate_metrics_modal.png";
import metricsIcon from "../../assets/svg/metrics_icon.svg";
import testingAnimationSvg from "../../assets/svg/testing_animation.svg";
import { Status } from "../../enums/Status";
import { ModalState, HeroKind } from "../../enums/Modal";
import { FileLocation, FileOptionTabTitle } from "../../types/fileUploadTypes";
import { ValidationResponse } from "../../types/validationTypes";
import { useParams } from "react-router-dom";
import { toSlug as slugify } from "../../utils/formattingUtils";
import {
  FILE_OPTION_ROUTES_MAPPING,
  FILE_VALIDATION_CONFIG,
} from "./constants";
import { useJobParams } from "../../hooks/useGetJobParams";
import {
  APIJobParam,
  JobParam,
  JobParameterName,
  RunJobConfig,
} from "../../models/JobModels";
import {
  CompassModal,
  ModalContent,
  ModalFooter,
  useCompassModal,
  defineModalConfig,
} from "../../components/common/Modal";

const StyledFormWrapper = styled.div`
  width: 100%;

  .ant-select-selector,
  .ant-picker {
    width: 100%;
  }

  .ant-form-item-label > label {
    color: ${greyText} !important;
  }
`;

type RunJobContext = { fileType: FileOptionTabTitle; dotCounter: number };

const renderDots = (dotCounter: number): React.ReactNode =>
  [1, 2, 3].map((index) => (
    <CompassParagraph
      key={index}
      color={dotCounter >= index ? darkGreen : "transparent"}
      mr={-2}
      fontSize={20}
      bold={true}
    >
      .
    </CompassParagraph>
  ));

const RUN_JOB_MODAL_CONFIG = defineModalConfig<ModalState, RunJobContext>({
  title: "Process File",
  titleIcon: metricsIcon,
  modalSize: "sm",
  states: {
    [ModalState.IDLE]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: RunJobModalImage,
        padding: 1,
        size: 400,
        alt: "Run Job",
        title: (ctx) =>
          FILE_OPTION_ROUTES_MAPPING[ctx.fileType].titleNotRunning,
        description: (ctx) =>
          FILE_OPTION_ROUTES_MAPPING[ctx.fileType].description,
      },
    },
    [ModalState.VALIDATING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: testingAnimationSvg,
        alt: "Validating",
        title: "Validating File",
        description: "Checking your file for errors",
        suffix: (ctx) => renderDots(ctx.dotCounter),
      },
    },
    [ModalState.RUNNING]: {
      isProcessing: true,
      hero: {
        size: 200,
        kind: HeroKind.LOTTIE,
        animation: calculatingAnimation,
        title: (ctx) => FILE_OPTION_ROUTES_MAPPING[ctx.fileType].title,
        description: (ctx) =>
          FILE_OPTION_ROUTES_MAPPING[ctx.fileType].descriptionNotRunning,
        suffix: (ctx) => renderDots(ctx.dotCounter),
      },
    },
  },
});

const JOB_PARAM_TO_API_PARAM: Record<JobParameterName, string> = {
  [JobParameterName.MANAGERS]: "manager_id",
  [JobParameterName.FUNDS]: "fund_id",
};

const convertJobParamsToAPIParams = (jobParams: JobParam[]): APIJobParam[] => {
  return jobParams.map((param) => ({
    parameterName: JOB_PARAM_TO_API_PARAM[param.parameterName],
    value: param.value,
  }));
};

export const RunJobModal: React.FC<ModalComponentProps> = ({
  show,
  handleClose,
  onValidationFailed,
}) => {
  const [form] = Form.useForm();
  const { uploadedFiles, deleteFile } = useUploadedFiles();
  const { fileType } = useParams();
  const { jobParamsOptions, isLoading, handleJobParamUpdate, jobParams } =
    useJobParams(fileType || null);
  const { addToast } = useToast();
  const { validate, isValidating } = useValidation();

  const {
    sendData: runJob,
    error,
    isLoading: isRunningJob,
  } = usePost<Record<string, never>, RunJobConfig>();
  const [hasCalculated, setHasCalculated] = useState<boolean>(false);
  const [dotCounter, setDotCounter] = useState<number>(0);

  const fileTypeName =
    Object.values(FileOptionTabTitle).find(
      (key) => slugify(key) === fileType,
    ) || FileOptionTabTitle.PORTFOLIO;

  const selectOptionsGroups = useMemo(() => {
    return jobParamsOptions.map((options) => ({
      selectorName: options[0]?.selectorName || "",
      options: options.map((opt) => ({
        key: opt.key,
        value: opt.key,
        label: opt.displayValue,
      })),
    }));
  }, [jobParamsOptions]);

  const handleSelectChange = (value: string, selectorName: string): void => {
    handleJobParamUpdate({
      key: value,
      displayValue: value,
      selectorName,
    });
  };

  // Dot animation interval
  useEffect(() => {
    const intervalId = setInterval(() => {
      setDotCounter((prev) => (prev + 1) % 4);
    }, 500);
    return () => clearInterval(intervalId);
  }, []);

  const modalState = isValidating
    ? ModalState.VALIDATING
    : isRunningJob
      ? ModalState.RUNNING
      : ModalState.IDLE;

  const { modalProps, isProcessing } = useCompassModal({
    config: RUN_JOB_MODAL_CONFIG,
    state: modalState,
    context: { fileType: fileTypeName, dotCounter },
  });

  useEffect(() => {
    if (error) {
      addToast(MESSAGES.JOB_RUN.ERROR, Status.ERROR);
    }
    if (hasCalculated && !error) {
      addToast(MESSAGES.JOB_RUN.SUCCESS, Status.SUCCESS);
      deleteFile(fileTypeName);
    }
  }, [error, hasCalculated]);

  const handleModalClose = (): void => {
    handleClose();
  };

  const runValidation = async (): Promise<boolean> => {
    const validationConfig = FILE_VALIDATION_CONFIG[fileTypeName];
    const location = uploadedFiles[fileTypeName].location as FileLocation;

    if (!validationConfig.enabled) {
      return true;
    }

    const validationParams = convertJobParamsToAPIParams(jobParams);
    const result = await validate(fileTypeName, location, validationParams);

    if (result?.validation_passed) {
      return true;
    } else if (result) {
      onValidationFailed(result);
      return false;
    }

    return false;
  };

  const startJob = async (): Promise<void> => {
    uploadedFiles[fileTypeName].location &&
      (await runJob(FILE_OPTION_ROUTES_MAPPING[fileTypeName].run, {
        location: uploadedFiles[fileTypeName].location as FileLocation,
        params: convertJobParamsToAPIParams(jobParams),
      }));
  };

  const handleStartJob = async (): Promise<void> => {
    const validationPassed = await runValidation();
    if (!validationPassed) {
      return;
    }

    await startJob();
    setHasCalculated(true);
    handleModalClose();
  };

  const footerContent = (
    <ModalFooter
      onClose={handleModalClose}
      closeText="Cancel"
      submitText="Go"
      onSubmit={(): void => form.submit()}
      isLoading={isProcessing}
      showDivider={false}
    />
  );

  return (
    <CompassModal
      show={show}
      handleClose={handleClose}
      {...modalProps}
      footer={footerContent}
    >
      <ModalContent>
        <StyledFormWrapper>
          <Form
            form={form}
            layout="vertical"
            size="large"
            requiredMark
            onFinish={handleStartJob}
          >
            {selectOptionsGroups.map((group, index) => (
              <Form.Item
                key={`${index}-${group.selectorName}-${group.options.length}`}
                name={group.selectorName}
                label={group.selectorName}
                initialValue={
                  group.options.length === 1
                    ? group.options[0].value
                    : undefined
                }
                rules={[
                  {
                    required: true,
                    message: `Please select ${group.selectorName}`,
                  },
                ]}
              >
                <Select
                  options={group.options}
                  placeholder={`Select ${group.selectorName}`}
                  loading={isLoading}
                  disabled={
                    isLoading || isProcessing || group.options.length === 1
                  }
                  onChange={(value): void =>
                    handleSelectChange(value as string, group.selectorName)
                  }
                />
              </Form.Item>
            ))}
          </Form>
        </StyledFormWrapper>
      </ModalContent>
    </CompassModal>
  );
};

interface ModalComponentProps {
  show: boolean;
  handleClose: () => void;
  onValidationFailed: (response: ValidationResponse) => void;
}
